import * as RadixContextMenu from "@radix-ui/react-context-menu";
import {
	type ForwardRefRenderFunction,
	type ReactNode,
	type RefAttributes,
	forwardRef,
} from "react";

import { ChevronRightIcon } from "@moment/icons";

import { MomentBasicTooltip } from "../MomentTooltip";
import { cn } from "../utils";

const ContextMenuContent: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixContextMenu.ContextMenuContentProps & RefAttributes<HTMLDivElement>
> = ({ children, ...props }, ref) => {
	return (
		<RadixContextMenu.Content
			{...props}
			ref={ref}
			className={cn(
				"data-[state=open]:animate-in",
				"data-[state=closed]:animate-out",
				"data-[state=closed]:fade-out-0",
				"data-[state=open]:fade-in-0",
				"data-[state=closed]:zoom-out-95",
				"data-[state=open]:zoom-in-95",
				"data-[side=bottom]:slide-in-from-top-2",
				"data-[side=left]:slide-in-from-right-2",
				"data-[side=right]:slide-in-from-left-2",
				"data-[side=top]:slide-in-from-bottom-2",
				"rounded-md",
				"shadow-xl",
				"z-popover",
				props.className
			)}
		>
			<div
				className={cn(
					"overflow-hidden",
					"rounded-md",
					"border",
					"border-secondary",
					"bg-primary-raised",
					"text-sm"
				)}
			>
				<div className="DropdownMenuContentScrollContainer max-h-[32rem] overflow-y-auto py-1">
					{children}
				</div>
			</div>
		</RadixContextMenu.Content>
	);
};

const ContextMenuItem: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixContextMenu.ContextMenuItemProps &
		RefAttributes<HTMLDivElement> & {
			variant?: "default" | "negative";
			disabledMessage?: ReactNode;
		}
> = ({ children, variant, className, disabledMessage, ...props }, ref) => {
	let variantClass: string;
	switch (variant) {
		case "negative":
			variantClass = "DropdownMenuItem DropdownMenuItem--negative";
			break;

		default:
			variantClass = "DropdownMenuItem DropdownMenuItem--default";
			break;
	}

	const item = (
		<RadixContextMenu.Item
			{...props}
			ref={ref}
			className={cn(
				variantClass,
				className,
				"group flex cursor-pointer flex-row items-center gap-2 px-2 py-1.5 mx-1 rounded"
			)}
		>
			{children}
		</RadixContextMenu.Item>
	);

	if (props.disabled && disabledMessage) {
		return (
			<MomentBasicTooltip
				content={<div className="max-w-[16rem]">{disabledMessage}</div>}
				contentProps={{ side: "right" }}
			>
				<button className="w-full cursor-default">{item}</button>
			</MomentBasicTooltip>
		);
	}

	return item;
};

const ContextMenuSubTrigger: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixContextMenu.ContextMenuSubTriggerProps &
		RefAttributes<HTMLDivElement> & { disabledMessage?: ReactNode }
> = ({ children, disabledMessage, ...props }, ref) => {
	const item = (
		<RadixContextMenu.SubTrigger
			{...props}
			ref={ref}
			className="DropdownMenuSubTrigger flex cursor-default flex-row items-center px-2 py-1.5 mx-1 rounded"
		>
			<div className="mr-2 flex flex-row items-center gap-2">{children}</div>
			<ChevronRightIcon className="ml-auto" />
		</RadixContextMenu.SubTrigger>
	);

	if (props.disabled && disabledMessage) {
		return (
			<MomentBasicTooltip
				content={<div className="max-w-[16rem]">{disabledMessage}</div>}
				contentProps={{ side: "right" }}
			>
				<button className="flex w-full cursor-default flex-col items-stretch">
					{item}
				</button>
			</MomentBasicTooltip>
		);
	}

	return item;
};

const ContextMenuSubContent: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixContextMenu.ContextMenuSubContentProps & RefAttributes<HTMLDivElement>
> = ({ children, sideOffset = 4, ...props }, ref) => {
	return (
		<RadixContextMenu.SubContent
			{...props}
			ref={ref}
			className={cn(
				"data-[state=open]:animate-in",
				"data-[state=closed]:animate-out",
				"data-[state=closed]:fade-out-0",
				"data-[state=open]:fade-in-0",
				"data-[state=closed]:zoom-out-95",
				"data-[state=open]:zoom-in-95",
				"data-[side=bottom]:slide-in-from-top-2",
				"data-[side=left]:slide-in-from-right-2",
				"data-[side=right]:slide-in-from-left-2",
				"data-[side=top]:slide-in-from-bottom-2",
				props.className,
				"-mt-1",
				"max-w-xs",
				"overflow-hidden",
				"rounded-md",
				"border",
				"py-1",
				"text-sm",
				"shadow-lg",
				"z-popover",
				"bg-primary-raised"
			)}
			sideOffset={sideOffset}
		>
			{children}
		</RadixContextMenu.SubContent>
	);
};

const ContextMenuLabel: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixContextMenu.ContextMenuLabelProps & React.HTMLAttributes<HTMLDivElement>
> = ({ children, ...props }, ref) => {
	return (
		<RadixContextMenu.Label
			{...props}
			ref={ref}
			className={cn(
				"DropdownMenuItem",
				"font-bold",
				"text-tertiary",
				"text-xs",
				"tracking-wider",
				"flex cursor-pointer flex-row items-center gap-2 px-2 py-1.5 mx-1 rounded"
			)}
		>
			{children}
		</RadixContextMenu.Label>
	);
};

const ContextMenuSeparator: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixContextMenu.ContextMenuSeparatorProps & RefAttributes<HTMLDivElement>
> = (_, ref) => {
	return <RadixContextMenu.Separator ref={ref} className="my-1 border-t" />;
};

/**
 * Styled variant of the Radix `ContextMenu` component.
 * Shares styling with `DropdownMenu` via the same CSS class names.
 */
export const ContextMenu = {
	Root: RadixContextMenu.Root,

	Trigger: RadixContextMenu.Trigger,

	Portal: RadixContextMenu.Portal,

	Content: forwardRef(ContextMenuContent),

	Label: forwardRef(ContextMenuLabel),
	Item: forwardRef(ContextMenuItem),

	Group: RadixContextMenu.Group,

	Sub: RadixContextMenu.Sub,
	SubTrigger: forwardRef(ContextMenuSubTrigger),
	SubContent: forwardRef(ContextMenuSubContent),

	Separator: forwardRef(ContextMenuSeparator),
};
